import {Helmet} from '@dr.pogodin/react-helmet';
import {openDeleteDialog} from 'platform/components';
import {match} from 'ts-pattern';

import {head} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {useDeleteCorrectiveTaxDocumentSettingMutation} from '@dms/api/finance';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {DmsDataGrid, handleApiError} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

export const CorrectiveTaxDocuments = () => {
  const navigate = useNavigate();
  const [deleteCorrectiveTaxDocument] = useDeleteCorrectiveTaxDocumentSettingMutation();

  const actionCallback: ActionCallback = ({actionKey, rowId, refreshData}) => {
    const id = isArray(rowId) ? head(rowId) : rowId;
    if (!id) {
      throw new Error('Invalid data for callback');
    }

    match(actionKey)
      .with('DELETE', () =>
        openDeleteDialog({
          onConfirm: () =>
            deleteCorrectiveTaxDocument({
              id,
            })
              .unwrap()
              .then(() => refreshData())
              .catch(handleApiError),
        })
      )
      .with('EDIT', 'DETAIL', () =>
        navigate(composePath(settingsRoutes.correctiveTaxDocumentsEdit, {params: {id}}))
      );
  };

  const title = i18n.t('entity.correctiveTaxDocumentSetting.labels.list');

  return (
    <>
      <Helmet title={title} />
      <SettingsTemplate
        data-testid={testIds.settings.correctiveTaxDocuments('list')}
        header={{
          title,
          subtitle: i18n.t('entity.correctiveTaxDocumentSetting.labels.listDescription'),
          actions: [
            {
              type: 'button',
              onClick: () => navigate(settingsRoutes.correctiveTaxDocumentsNew),
              title: i18n.t('entity.correctiveTaxDocumentSetting.labels.add'),
            },
          ],
        }}
      >
        <DmsDataGrid
          gridCode="accounting-corrective-tax-document-setting"
          actionCallback={actionCallback}
          data-testid={testIds.settings.correctiveTaxDocuments('dg')}
        />
      </SettingsTemplate>
    </>
  );
};
